import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  LucideAngularModule,
  BarChart3,
  ChevronDown,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Users,
} from 'lucide-angular';
import { QuestionAnalysis, ItemAnalysis } from '../../../grading.types';

/**
 * Student feedback for a specific question
 */
export interface StudentQuestionFeedback {
  studentId: string;
  studentName: string;
  score: {
    earnedPoints: number;
    maxPoints: number;
    percentage: number;
    gradeLetter: string;
  };
  feedback: string;
}

/**
 * Per-question student feedback map
 * Key is itemId, value is array of student feedback for students with < A grade
 */
export type QuestionStudentFeedbackMap = Map<string, StudentQuestionFeedback[]>;

@Component({
  selector: 'app-question-analysis',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './question-analysis.component.html',
  styleUrl: './question-analysis.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionAnalysisComponent {
  readonly analysis = input.required<QuestionAnalysis>();

  /** Per-question student feedback for students with < A grade */
  readonly studentFeedbackMap = input<QuestionStudentFeedbackMap>(new Map());

  /** Loading state for student feedback */
  readonly isLoadingFeedback = input<boolean>(false);

  protected readonly icons = {
    BarChart3,
    ChevronDown,
    AlertCircle,
    CheckCircle2,
    XCircle,
    Users,
  };

  // Track expanded sections
  protected readonly expandedSections = signal<Set<string>>(new Set(['section-0']));

  // Track expanded items (for "Show more" functionality)
  protected readonly expandedItems = signal<Set<string>>(new Set());

  // Track expanded student feedback sections
  protected readonly expandedStudentFeedback = signal<Set<string>>(new Set());

  /**
   * Get sorted sections by order
   */
  protected readonly sortedSections = computed(() => {
    const sections = [...this.analysis().sections];
    return sections.sort((a, b) => a.order - b.order);
  });

  /**
   * Toggle section expansion
   */
  protected toggleSection(sectionId: string): void {
    const current = this.expandedSections();
    const newSet = new Set(current);
    if (newSet.has(sectionId)) {
      newSet.delete(sectionId);
    } else {
      newSet.add(sectionId);
    }
    this.expandedSections.set(newSet);
  }

  /**
   * Check if section is expanded
   */
  protected isSectionExpanded(sectionId: string): boolean {
    return this.expandedSections().has(sectionId);
  }

  /**
   * Toggle item expansion (show more/less)
   */
  protected toggleItem(itemId: string): void {
    const current = this.expandedItems();
    const newSet = new Set(current);
    if (newSet.has(itemId)) {
      newSet.delete(itemId);
    } else {
      newSet.add(itemId);
    }
    this.expandedItems.set(newSet);
  }

  /**
   * Check if item is expanded
   */
  protected isItemExpanded(itemId: string): boolean {
    return this.expandedItems().has(itemId);
  }

  /**
   * Toggle student feedback section expansion
   */
  protected toggleStudentFeedback(itemId: string): void {
    const current = this.expandedStudentFeedback();
    const newSet = new Set(current);
    if (newSet.has(itemId)) {
      newSet.delete(itemId);
    } else {
      newSet.add(itemId);
    }
    this.expandedStudentFeedback.set(newSet);
  }

  /**
   * Check if student feedback section is expanded
   */
  protected isStudentFeedbackExpanded(itemId: string): boolean {
    return this.expandedStudentFeedback().has(itemId);
  }

  /**
   * Get student feedback for a specific item
   */
  protected getStudentFeedback(itemId: string): StudentQuestionFeedback[] {
    return this.studentFeedbackMap().get(itemId) || [];
  }

  /**
   * Get score color class for student feedback
   */
  protected getScoreColorClass(gradeLetter: string): string {
    switch (gradeLetter) {
      case 'B':
        return 'text-green-600';
      case 'C':
        return 'text-yellow-600';
      case 'D':
        return 'text-orange-600';
      case 'F':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  }

  /**
   * Grade colors for progress bar and legend
   */
  private readonly gradeColors = {
    A: { colorClass: 'bg-green-500', dotClass: 'bg-green-500' },
    B: { colorClass: 'bg-blue-500', dotClass: 'bg-blue-500' },
    C: { colorClass: 'bg-yellow-400', dotClass: 'bg-yellow-400' },
    D: { colorClass: 'bg-orange-400', dotClass: 'bg-orange-400' },
    F: { colorClass: 'bg-red-400', dotClass: 'bg-red-400' },
  };

  /**
   * Calculate grade widths for progress bar
   */
  protected getGradeWidths(item: ItemAnalysis): Array<{ grade: string; width: number; colorClass: string }> {
    const total = item.totalStudents;
    if (total === 0) return [];

    const dist = item.gradeDistribution;
    return [
      { grade: 'A', width: (dist.gradeA / total) * 100, colorClass: this.gradeColors.A.colorClass },
      { grade: 'B', width: (dist.gradeB / total) * 100, colorClass: this.gradeColors.B.colorClass },
      { grade: 'C', width: (dist.gradeC / total) * 100, colorClass: this.gradeColors.C.colorClass },
      { grade: 'D', width: (dist.gradeD / total) * 100, colorClass: this.gradeColors.D.colorClass },
      { grade: 'F', width: (dist.gradeF / total) * 100, colorClass: this.gradeColors.F.colorClass },
    ];
  }

  /**
   * Get grade legend data
   */
  protected getGradeLegend(item: ItemAnalysis): Array<{ grade: string; count: number; dotClass: string }> {
    const dist = item.gradeDistribution;
    return [
      { grade: 'A', count: dist.gradeA, dotClass: this.gradeColors.A.dotClass },
      { grade: 'B', count: dist.gradeB, dotClass: this.gradeColors.B.dotClass },
      { grade: 'C', count: dist.gradeC, dotClass: this.gradeColors.C.dotClass },
      { grade: 'D', count: dist.gradeD, dotClass: this.gradeColors.D.dotClass },
      { grade: 'F', count: dist.gradeF, dotClass: this.gradeColors.F.dotClass },
    ];
  }

  /**
   * Format average score display
   */
  protected formatAvgScore(item: ItemAnalysis): string {
    return `${item.averageScore}/${item.maxScore}`;
  }
}
